<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('attendance_management')" :icon="'calendar-check'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <div class="btn-group">
                        <button 
                            type="button" 
                            class="btn btn-primary btn-with-shadow"
                            @click="navigateTo('dashboard')"
                        >
                            <i class="fas fa-tachometer-alt mr-2"></i>
                            {{ $t('dashboard') }}
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-success btn-with-shadow"
                            @click="navigateTo('realtime')"
                        >
                            <i class="fas fa-activity mr-2"></i>
                            {{ $t('real_time') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Quick Overview Cards -->
        <div class="row mb-4">
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card bg-primary text-white" @click="navigateTo('dashboard')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('daily_summary') }}</h6>
                                <p class="mb-0 small">{{ $t('comprehensive_daily_report') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-chart-pie fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card bg-success text-white" @click="navigateTo('employee-details')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('employee_details') }}</h6>
                                <p class="mb-0 small">{{ $t('detailed_employee_attendance') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-users fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card bg-secondary text-white" @click="navigateTo('monthly-calendar')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('monthly_attendance_calendar') }}</h6>
                                <p class="mb-0 small">{{ $t('monthly_calendar_view') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-calendar-alt fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card bg-warning text-white" @click="navigateTo('realtime')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('real_time_monitoring') }}</h6>
                                <p class="mb-0 small">{{ $t('live_attendance_tracking') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-activity fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Deuxième rangée -->
        <div class="row mb-4">
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card bg-info text-white" @click="navigateTo('reports')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('attendance_reports') }}</h6>
                                <p class="mb-0 small">{{ $t('generate_export_reports') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-file-alt fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Today's Quick Stats -->
        <div class="row mb-4" v-if="todayStats">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('todays_overview') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-3 text-center">
                                <h4 class="text-success">{{ todayStats.present_employees || 0 }}</h4>
                                <p class="mb-0">{{ $t('present_employees') }}</p>
                            </div>
                            <div class="col-md-3 text-center">
                                <h4 class="text-danger">{{ todayStats.absent_employees || 0 }}</h4>
                                <p class="mb-0">{{ $t('absent_employees') }}</p>
                            </div>
                            <div class="col-md-3 text-center">
                                <h4 class="text-warning">{{ todayStats.late_arrivals || 0 }}</h4>
                                <p class="mb-0">{{ $t('late_arrivals') }}</p>
                            </div>
                            <div class="col-md-3 text-center">
                                <h4 class="text-info">{{ todayStats.overtime_employees || 0 }}</h4>
                                <p class="mb-0">{{ $t('overtime_employees') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Quick Actions -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('quick_actions') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-primary btn-block" @click="navigateTo('dashboard')">
                                    <i class="fas fa-tachometer-alt mr-2"></i>
                                    {{ $t('view_dashboard') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-success btn-block" @click="navigateTo('employee-details')">
                                    <i class="fas fa-users mr-2"></i>
                                    {{ $t('employee_details') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-warning btn-block" @click="navigateTo('realtime')">
                                    <i class="fas fa-activity mr-2"></i>
                                    {{ $t('real_time_monitoring') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-info btn-block" @click="exportTodayReport">
                                    <i class="fas fa-file-export mr-2"></i>
                                    {{ $t('export_today_report') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { ATTENDANCE_DAILY_SUMMARY, ATTENDANCE_EXPORT_SUMMARY } from '../../../Config/ApiUrl';

export default {
    extends: CoreLibrary,
    name: "AttendanceNavigation",
    data() {
        return {
            todayStats: null
        }
    },
    mounted() {
        this.loadTodayStats();
    },
    methods: {
        navigateTo(route) {
            const routes = {
                'dashboard': '/attendance/dashboard-summary',
                'employee-details': '/attendance/employee-details',
                'monthly-calendar': '/attendance/monthly-calendar',
                'realtime': '/attendance/real-time-stats',
                'reports': '/attendance/reports'
            };
            
            if (routes[route]) {
                window.location.href = routes[route];
            }
        },

        async loadTodayStats() {
            try {
                const response = await this.axiosGet(ATTENDANCE_DAILY_SUMMARY);
                this.todayStats = response.data;
            } catch (error) {
                console.error('Error loading today stats:', error);
            }
        },

        exportTodayReport() {
            const today = window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0];
            const url = `/attendances/export-summary?date=${today}`;
            window.open(url, '_blank');
        }
    }
}
</script>

<style scoped>
.overview-card {
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    border: none;
    border-radius: 10px;
}

.overview-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(0,0,0,0.15);
}

.overview-icon {
    opacity: 0.8;
}

.card {
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}
</style>
